import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useDebounce} from '../../../../hooks/useDebounce';
import Button from '../../../../incase-domain/components/button/Button';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import {IElementTextInput} from '../../../../incase-domain/elements/textInput';
import {ElementInteraction, InputElementInteractions} from '../../../../incase-domain/proptypes/runfile';
import {updateArrowsDisabledAction} from '../../../../store/actions/presentation.actions';
import {runFileElementInteractionsSelector} from '../../../../store/selectors/runfile.selectors';

const VALIDATION_ERROR_KEYS = {
    email: 'validations.email',
    numeric: 'validations.numeric',
    phone: 'validations.phone',
} as const;

interface ElementTextInputProps {
    element: IElementTextInput;
    savePresentationRunFile?: (elementRunFile: ElementInteraction, newPoints?: number) => void;
    device?: DeviceType;
    setValidated?: (error: boolean) => void;
}

function ElementTextInput(props: ElementTextInputProps): ReactElement {
    const {element, device = DeviceType.DESKTOP, savePresentationRunFile, setValidated} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const debounce = useDebounce(200);

    const elementInteractions = useSelector(runFileElementInteractionsSelector);
    const slideElements = (elementInteractions && elementInteractions[element.slideId]) || {};
    const textInputElement = (slideElements[element.id] || {}) as InputElementInteractions;

    const showConfirm = !!element.confirmButton && !!savePresentationRunFile;
    const submitted = showConfirm && !!textInputElement.answered;
    const confirmLock = useRef(false);

    const [inputValue, setInputValue] = useState(textInputElement.inputValue || '');
    const [regexConfirmed, setRegexConfirmed] = useState(false);
    const [firstTry, setFirstTry] = useState(true);

    const showError = !regexConfirmed && !firstTry && !!element.regex;
    const validationErrorKey = VALIDATION_ERROR_KEYS[element.validationType as keyof typeof VALIDATION_ERROR_KEYS];
    const errorMessage = validationErrorKey ? t(validationErrorKey) : element.regexErrorMessage;

    useEffect(() => {
        if (element.regex && textInputElement?.inputValue) {
            setInputValue(textInputElement.inputValue);
            setFirstTry(false);
            setRegexConfirmed(new RegExp(element.regex).test(textInputElement.inputValue));
        }
        setValidated && setValidated(!element.regex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element.regex]);

    useEffect(() => {
        if (submitted) {
            setInputValue(textInputElement.inputValue || '');
        }
    }, [submitted, textInputElement.inputValue]);

    const handleSavePresentationRunFile = (value: string, answered: boolean): void =>
        savePresentationRunFile && debounce(() => savePresentationRunFile({inputValue: value, answered}));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (submitted) {
            return;
        }
        setInputValue(e.target.value);

        if (element.regex) {
            const regexChecked = new RegExp(element.regex).test(e.target.value);
            setRegexConfirmed(regexChecked);
            setValidated && setValidated(regexChecked);
            if (!showConfirm) {
                handleSavePresentationRunFile(e.target.value, e.target.value.length > 0 && regexChecked);
            }
        } else if (!showConfirm) {
            handleSavePresentationRunFile(e.target.value, e.target.value.length > 0);
        }
    };

    const handleConfirm = (): void => {
        if (submitted || confirmLock.current || !savePresentationRunFile) {
            return;
        }

        const value = inputValue.trim();
        if (!value) {
            return;
        }

        if (element.regex) {
            const regexChecked = new RegExp(element.regex).test(value);
            setRegexConfirmed(regexChecked);
            setFirstTry(false);
            setValidated && setValidated(regexChecked);
            if (!regexChecked) {
                return;
            }
        }

        confirmLock.current = true;
        savePresentationRunFile({inputValue: value, answered: true});
    };

    return (
        <div className={getElementClasses(device)} style={getElementStyle(device, element)}>
            <label className="c-inputElement__field">
                <div className="c-inputElement__label">{element.label}</div>
                <input
                    name={element.htmlName || element.label}
                    type="text"
                    className={`c-inputElement__input ${showError ? 'c-inputElement__input--error' : ''}`}
                    placeholder={element.placeholder || 'Placeholder text'}
                    value={inputValue}
                    disabled={submitted}
                    onChange={handleChange}
                    onFocus={() => dispatch(updateArrowsDisabledAction(true))}
                    onBlur={() => {
                        dispatch(updateArrowsDisabledAction(false));
                        if (firstTry) setFirstTry(false);
                    }}
                />
                {showError && <div className="c-inputElement__error">{errorMessage}</div>}
            </label>
            {showConfirm && (
                <Button
                    type="button"
                    className="c-inputElement__confirm"
                    size="sm"
                    color="info"
                    fullWidth
                    dynamicSize
                    disabled={submitted || !inputValue.trim()}
                    disablePointerEvents={submitted}
                    leading={submitted ? 'solar:check-read-linear' : undefined}
                    onClick={handleConfirm}
                >
                    {submitted ? t('common.done') : t('common.confirm')}
                </Button>
            )}
        </div>
    );
}

function getElementStyle(device: DeviceType, element: IElementTextInput): React.CSSProperties {
    if (device === DeviceType.MOBILE) {
        return {
            fontFamily: element.fontFamily?.name,
        };
    }

    return {fontSize: `${element.font.size || 2}em`, fontFamily: element.fontFamily?.name};
}

function getElementClasses(device: DeviceType): string {
    if (device === DeviceType.MOBILE) {
        return 'c-inputElement c-inputElement--mobile';
    }

    return 'c-inputElement';
}

export default ElementTextInput;
